import React, {useEffect, useState} from 'react';

import { useTranslationService } from 'src/services/TranslationService';
import {IconInvestor, IconSuccess, IconTrading, CompareRegulatedCompaniesModal} from "@darwinex/components-library";
import IconCheckCircle from "src/components/icons/IconCheckCircle";
import { IconClose } from "src/components/icons/IconClose";
import {regulatedCompaniesEnum} from "../../../../enums/regulated-companies";
import {useProfileService} from "../../../../services/ProfileService";

const iconProps = {
    width: 16,
    height: 16,
};

const CATEGORY = {
    INVESTOR: 'investor',
    TRADER: 'trader',
    ANY: 'any',
}

export const CompareRegulatedCompanies = ({
    onClose,
    regulatedCompanies,
    regulatedCompany,
    setRegulatedCompany,
}) => {
    const { profile } = useProfileService();

    const { t } = useTranslationService();

    const [category, setCategory] = useState(CATEGORY.TRADER);
    const [selectedRegCo, setSelectedRegCo] = useState(regulatedCompany);

    const getRegulatedCompaniesAvailable = (cat) => {
        if (cat === CATEGORY.INVESTOR) {
            return regulatedCompanies.filter(item => item !== regulatedCompaniesEnum.DX_SYC);
        }
        return regulatedCompanies;
    }

    const regulatedCompaniesAvailable = getRegulatedCompaniesAvailable(category);

    const closeModal = () => {
        if (!profile) {
            setRegulatedCompany(selectedRegCo);
        }
        onClose();
    }

    const save = () => {
        setRegulatedCompany(selectedRegCo);
        onClose();
    }

    const loggedInProps = profile && regulatedCompany !== selectedRegCo ? {
        loggedIn: true,
        extra_content: {
            legal_terms: selectedRegCo === regulatedCompaniesEnum.DX_FCA ? t('modal-regulated-companies.legal.FCA', { uri: '/legal/terms-conditions?reg=dx_fca' })
                : (selectedRegCo === regulatedCompaniesEnum.DX_CNMV ? t('modal-regulated-companies.legal.CNMV', { uri: '/legal/terms-conditions?reg=dx_cnmv' })
                    : t('modal-regulated-companies.legal.SYC', { uri: '/legal/terms-conditions?reg=dx_syc' })),
            commercial_communications: t(`new-login.allow-for-commercial-communications-${selectedRegCo}`),
            cancel_cta: t('modal-regulated-companies.cancel'),
            continue_cta: t('modal-regulated-companies.continue'),
        },
        onClickCancel: () => onClose(),
        onClickContinue: () => save(),
    } : {};

    const labelsFiltered = () => {
        const labels = [
            t('modal-regulated-companies.row.regulated'),
            t('modal-regulated-companies.row.availability'),
            t('modal-regulated-companies.row.deposit'),
            t('modal-regulated-companies.row.payment'),
            category !== CATEGORY.INVESTOR ? t('modal-regulated-companies.row.metatrader') : null,
            category !== CATEGORY.INVESTOR ? t('modal-regulated-companies.row.ibkr') : null,
            category !== CATEGORY.INVESTOR ? t('modal-regulated-companies.row.darwinia') : null,
            category !== CATEGORY.INVESTOR ? t('modal-regulated-companies.row.capital') : null,
            category !== CATEGORY.INVESTOR ? t('modal-regulated-companies.row.levarage') : null,
            category !== CATEGORY.INVESTOR ? t('modal-regulated-companies.row.protection') : null,
            category !== CATEGORY.TRADER ? t('modal-regulated-companies.row.invest') : null,
        ];

        return labels.filter((value) => value !== null);
    }

    const contentFiltered = () => {
        const content = {
            DX_FCA: [
                t('DX_FCA'),
                t('DX_FCA.info'),
                t('modal-regulated-companies.row.availability.FCA'),
                t('modal-regulated-companies.row.deposit.FCA'),
                t('modal-regulated-companies.row.payment.FCA'),
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? '1:30' : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.TRADER ? <IconCheckCircle {...iconProps} /> : null,
            ],
            DX_CNMV: [
                t('DX_CNMV'),
                t('DX_CNMV.info'),
                t('modal-regulated-companies.row.availability.CNMV'),
                t('modal-regulated-companies.row.deposit.CNMV'),
                t('modal-regulated-companies.row.payment.CNMV'),
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? '1:30' : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.TRADER ? <IconCheckCircle {...iconProps} /> : null,
            ],
            DX_SYC: [
                t('DX_SYC'),
                t('DX_SYC.info'),
                t('modal-regulated-companies.row.availability.SYC'),
                <IconClose {...iconProps} />,
                t('modal-regulated-companies.row.payment.SYC'),
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? <IconCheckCircle {...iconProps} /> : null,
                category !== CATEGORY.INVESTOR ? '1:200' : null,
                category !== CATEGORY.INVESTOR ? <IconClose {...iconProps} /> : null,
                category !== CATEGORY.TRADER ? <IconClose {...iconProps} /> : null,
            ],
        };

        Object.keys(content).forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(content, key)) {
                content[key] = content[key].filter(value => value !== null);
            }
        });

        return content;
    }

    const categoryButtonsFiltered = () => {
        const categoryButtons = [
            getRegulatedCompaniesAvailable(CATEGORY.TRADER).length > 0 ? {
                key: CATEGORY.TRADER,
                content: t('modal-regulated-companies.category.trader'),
                icon: <IconTrading {...iconProps} />,
            } : null,
            getRegulatedCompaniesAvailable(CATEGORY.INVESTOR).length > 0 ? {
                key: CATEGORY.INVESTOR,
                content: t('modal-regulated-companies.category.investor'),
                icon: <IconInvestor {...iconProps} />,
            } : null,
            getRegulatedCompaniesAvailable(CATEGORY.INVESTOR).length > 0 ? {
                key: CATEGORY.ANY,
                content: t('modal-regulated-companies.category.any'),
                icon: (
                    <span className="d-flex align-items-center dx-gx-2">
                                <IconTrading {...iconProps} /> + <IconInvestor {...iconProps} />
                            </span>
                ),
            } : null,
        ];

        return categoryButtons.filter((value) => value !== null);
    }

    return (
        <CompareRegulatedCompaniesModal
            content={{
                title: t('modal-regulated-companies.title'),
                description:
                    t('modal-regulated-companies.description'),
                categoryButtons: categoryButtonsFiltered(),
                table: {
                    labels: labelsFiltered(),
                    content: contentFiltered(),
                },
                table_cta: t('modal-regulated-companies.select'),
                table_cta_selected: (
                    <span className="d-flex dx-gx-3 align-items-center justify-content-center">
                        <IconSuccess {...iconProps} />
                        { t('modal-regulated-companies.selected') }
                    </span>
                ),
                recommended_regCo_text: t('modal-regulated-companies.recommended'),
                bottomDisclaimer: (
                    <>
                        { t('modal-regulated-companies.disclaimer') }
                    </>
                ),
            }}
            regulatedCompanies={regulatedCompaniesAvailable}
            recommendedRegCo={regulatedCompaniesAvailable.length > 0 ? regulatedCompaniesAvailable[0] : ''}
            onModalClose={() => closeModal()}
            category={category}
            onClickCategory={setCategory}
            selectedRegCo={selectedRegCo}
            onSelectNewRegCo={sel => setSelectedRegCo(sel)}
            {...loggedInProps}
        />
    );
};
